import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, X, Flame, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, ukDate, errorMessage } from "../../lib/api";

const PRESETS = [
  { key: "7d", label: "1 week", days: 7 },
  { key: "30d", label: "1 month", days: 30 },
  { key: "90d", label: "3 months", days: 90 },
  { key: "365d", label: "1 year", days: 365 },
  { key: "custom", label: "Custom", days: null },
];

function PriceCell({ price, unitPrice, unitLabel, date, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="font-mono text-sm font-semibold text-slate-900">
        {unitPrice != null && unitLabel && unitLabel !== "per pack" ? (
          <>{gbp(unitPrice)} <span className="text-[11px] font-sans font-normal text-slate-500">{unitLabel}</span></>
        ) : (
          gbp(price)
        )}
      </p>
      {unitPrice != null && unitLabel && unitLabel !== "per pack" && price !== unitPrice && (
        <p className="text-[11px] text-slate-400 font-mono">raw: {gbp(price)}</p>
      )}
      <p className="text-[11px] text-slate-500 mt-0.5">{ukDate(date)}</p>
    </div>
  );
}

function RiserRow({ a, rank, onDismiss }) {
  const unit = a.unit_label && a.unit_label !== "per pack" ? a.unit_label : null;
  return (
    <li className="grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 px-4 py-3 items-start" data-testid={`riser-${a.supplier_product_id}`}>
      <span className="pt-1 w-6 shrink-0 text-right font-mono text-xs text-slate-400">{rank}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <Link to={`/products/${a.supplier_product_id}`} className="font-medium text-slate-900 truncate hover:underline" data-testid={`riser-link-${a.supplier_product_id}`}>{a.description}</Link>
          {a.frequent && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"><Flame className="h-3 w-3" /> bought often</span>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 truncate">{a.supplier_name}{a.pack_size ? ` · ${a.pack_size}` : ""}</p>
        <div className="mt-2 grid grid-cols-[1fr_auto_1fr] gap-3 items-start">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Old price</p>
            <PriceCell price={a.old_price} unitPrice={a.old_unit_price} unitLabel={unit} date={a.old_date} testId={`riser-old-${a.supplier_product_id}`} />
          </div>
          <span className="text-slate-300 self-center font-mono text-xs">→</span>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">New price</p>
            <PriceCell price={a.new_price} unitPrice={a.new_unit_price} unitLabel={unit} date={a.new_date} testId={`riser-new-${a.supplier_product_id}`} />
          </div>
        </div>
      </div>
      <div className="flex items-start gap-1">
        <span className="font-mono text-sm font-semibold text-rose-600 whitespace-nowrap" data-testid={`riser-pct-${a.supplier_product_id}`}>+{a.pct}%</span>
        <button onClick={() => onDismiss(a)} className="p-1 text-slate-300 hover:text-slate-600" title="Dismiss" data-testid={`riser-dismiss-${a.supplier_product_id}`}><X className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

export default function PriceAlerts({ restaurantId }) {
  const [preset, setPreset] = useState(() => localStorage.getItem("supplivo_risers_preset") || "30d");
  const [customStart, setCustomStart] = useState(() => localStorage.getItem("supplivo_risers_from") || "");
  const [customEnd, setCustomEnd] = useState(() => localStorage.getItem("supplivo_risers_to") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { restaurant_id: restaurantId || undefined };
    const match = PRESETS.find((x) => x.key === preset);
    if (match && match.days) p.days = match.days;
    if (preset === "custom" && customStart && customEnd) { p.start = customStart; p.end = customEnd; }
    return p;
  }, [preset, customStart, customEnd, restaurantId]);

  const load = useCallback(() => {
    if (preset === "custom" && (!customStart || !customEnd)) { setData({ items: [] }); return; }
    setLoading(true);
    api.get("/insights/risers", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [] })).finally(() => setLoading(false));
  }, [params, preset, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("supplivo_risers_preset", preset); }, [preset]);
  useEffect(() => { if (customStart) localStorage.setItem("supplivo_risers_from", customStart); }, [customStart]);
  useEffect(() => { if (customEnd) localStorage.setItem("supplivo_risers_to", customEnd); }, [customEnd]);

  const dismiss = async (a) => {
    try {
      await api.post(`/insights/alerts/${encodeURIComponent(a.id)}/dismiss`);
      setData((d) => ({ ...d, items: (d?.items || []).filter((x) => x.id !== a.id) }));
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const activePreset = PRESETS.find((p) => p.key === preset) || PRESETS[1];
  const items = data?.items || [];

  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="price-alerts">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-rose-600" /> Price rises</h2>
          <p className="text-xs text-slate-500">The 10 products that went up the most inside the selected window — baseline and latest invoice both have to sit inside it.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label="Compare window" data-testid="risers-period">
          {PRESETS.map((p) => (
            <button key={p.key} role="radio" aria-checked={preset === p.key} onClick={() => setPreset(p.key)}
              className={`text-xs font-medium px-2.5 py-1 rounded-full border ${preset === p.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
              data-testid={`risers-preset-${p.key}`}>
              {p.key === "custom" ? <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{p.label}</span> : p.label}
            </button>
          ))}
        </div>
      </div>
      {preset === "custom" && (
        <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <label className="flex items-center gap-1">From<input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="rounded border border-slate-200 px-2 py-1" data-testid="risers-from" /></label>
          <label className="flex items-center gap-1">To<input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="rounded border border-slate-200 px-2 py-1" data-testid="risers-to" /></label>
          {(!customStart || !customEnd) && <span className="text-slate-400">Pick both dates to compare</span>}
        </div>
      )}
      {loading && !data ? <p className="p-6 text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <p className="p-6 text-sm text-slate-500" data-testid="risers-empty">
          No price rises in {activePreset.label.toLowerCase()}{preset === "custom" && customStart && customEnd ? ` (${ukDate(customStart)} → ${ukDate(customEnd)})` : ""}.
        </p>
      ) : (
        <ol className="divide-y divide-slate-100">{items.map((a, i) => <RiserRow key={a.id} a={a} rank={i + 1} onDismiss={dismiss} />)}</ol>
      )}
    </section>
  );
}
